import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, ButtonProps, Container, Stack, StackProps, Typography } from '@mui/material';
import { isNil } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';

import { useCustomerInfo } from '@/api/customers';
import { AlertChip } from '@/components/AlertChip';
import { DisplayCurrency, DisplayCurrencyProps } from '@/components/DisplayCurrency';
import { Null } from '@/components/Null';
import { Timestamp } from '@/components/data/Timestamp';
import { PaymentEmailCard } from '@/components/subscriptions/PaymentEmailCard';
import { PaymentMethodCard } from '@/components/subscriptions/PaymentMethodCard';
import { ConjoinedSummaryCards, SummaryCard, SummaryCardBody, SummaryCardRow } from '@/components/ui/SummaryCard';
import { useTransactionContext } from '@/context/transaction';
import { boldWeight } from '@/theme';
import { errorMessage } from '@/utils';

export type TransactionModalProps = { onBackClick?: ButtonProps['onClick']; children?: ReactNode };

export const TransactionModal = ({ onBackClick, children }: TransactionModalProps) => {
    const transactionContext = useTransactionContext();
    const { type, backTarget, hasPayment } = transactionContext;
    const [paymentMethodId, setPaymentMethodId] = useState<string>();

    useEffect(() => {
        if (type === 'payment' && !transactionContext.isPending && paymentMethodId) {
            transactionContext.setPaymentMethod(paymentMethodId);
        }
    }, [type, transactionContext.isPending, paymentMethodId, transactionContext]);

    // Treat 0 due as refund since there's no reason to prompt them for payment method
    const isRefund = !transactionContext.isPending && (transactionContext.totalDue ?? 0) <= 0;

    return (
        <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 3 }, mt: 2, mb: 4 }}>
            {backTarget && (
                <Button
                    href={onBackClick ? undefined : backTarget.href}
                    onClick={onBackClick}
                    sx={{ alignSelf: 'start', position: 'relative', left: '-12px', mb: -1 }}
                >
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                        <ArrowBackIcon sx={{ mr: 0.5, fontSize: '1.5rem' }} />
                        Back to {backTarget.label}
                    </Typography>
                </Button>
            )}
            {children}
            <SummaryCardRow>
                {isRefund ? (
                    <CurrentAccountBalanceCard />
                ) : (
                    <PaymentMethodCard
                        type={type}
                        immediatePaymentDue={!hasPayment}
                        onChangePaymentMethod={(paymentMethod) =>
                            type === 'payment' && setPaymentMethodId(paymentMethod.id)
                        }
                    />
                )}
                <PaymentEmailCard />
            </SummaryCardRow>
            {type === 'subscription' && <NextInvoiceTotalSummary />}
        </Container>
    );
};

export const NextInvoiceTotalSummary = () => {
    const { isPending, ...context } = useTransactionContext('subscription');

    if (isPending) return false;

    const { nextInvoiceTotal, currency, billingDate, isCancellation } = context;

    if (isCancellation) return <Null data-testid="no-next-invoice-on-cancel" />;

    return (
        <ConjoinedSummaryCards data-testid="next-invoice-total">
            <SummaryCard title="Next recurring invoice total">
                <SummaryCardBody>
                    <DisplayCurrency value={nextInvoiceTotal} currency={currency} cents stickyValueOnLoading />
                </SummaryCardBody>
            </SummaryCard>
            <SummaryCard sx={{ justifyContent: 'center' }}>
                <Typography variant="body2">
                    You will be charged the full monthly total starting on your next invoice on{' '}
                    <strong>
                        <Timestamp value={billingDate} unit="date" />
                    </strong>
                    .
                </Typography>
            </SummaryCard>
        </ConjoinedSummaryCards>
    );
};

export const TotalDueReadout = ({
    sx,
}: { displayCurrencyVariant?: DisplayCurrencyProps['variant'] } & Pick<StackProps, 'sx'>) => {
    const context = useTransactionContext();
    const { isPending, totalDueLabel } = context;
    const {
        totalDue = undefined,
        paymentDue = undefined,
        currency = undefined,
        isUpdating = true,
    } = isPending ? {} : context;

    const drawnFromBalance = !isNil(totalDue) && totalDue > 0 && !isNil(paymentDue) && paymentDue < totalDue;

    return (
        <Stack sx={{ gap: 1 }}>
            <Stack
                direction="row"
                data-testid="total-due-readout"
                sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, ...sx }}
            >
                <Typography sx={{ fontWeight: drawnFromBalance ? undefined : boldWeight }}>{totalDueLabel}:</Typography>
                <DisplayCurrency
                    variant={drawnFromBalance ? 'body1' : 'total'}
                    value={isUpdating ? undefined : totalDue}
                    stickyValueOnLoading
                    cents
                    currency={currency}
                />
            </Stack>
            {drawnFromBalance && (
                <>
                    <Stack
                        direction="row"
                        data-testid="balance-due-readout"
                        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, ...sx }}
                    >
                        <Typography>Credit from account balance:</Typography>
                        <DisplayCurrency
                            variant="body1"
                            value={isUpdating ? undefined : totalDue - paymentDue}
                            negative
                            stickyValueOnLoading
                            cents
                            currency={currency}
                        />
                    </Stack>
                    <Stack
                        direction="row"
                        data-testid="payment-due-readout"
                        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, ...sx }}
                    >
                        <Typography sx={{ fontWeight: boldWeight }}>Due from payment method:</Typography>
                        <DisplayCurrency
                            variant="total"
                            value={isUpdating ? undefined : paymentDue}
                            stickyValueOnLoading
                            cents
                            currency={currency}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    );
};

/**
 * Not using AccountBalanceCard because:
 * - We want a different title ("Current")
 * - We don't want the subtext estimating how long it will last
 *
 * This is just used in place of the "Payment method" card if they're getting a refund, so we don't imply that the refund is going back to their card.
 */
const CurrentAccountBalanceCard = () => {
    const { data: customerInfo, isError, error, isPending } = useCustomerInfo();

    return (
        <SummaryCard
            title="Current account balance"
            isLoading={isPending}
            data-testid="current-account-balance-content"
        >
            <SummaryCardBody>
                {isError ? (
                    <AlertChip severity="error" label={errorMessage(error, 'Unable to load account balance')} />
                ) : (
                    <DisplayCurrency
                        value={customerInfo?.data?.balance}
                        currency={customerInfo?.data?.currency}
                        negative
                        cents
                    />
                )}
            </SummaryCardBody>
        </SummaryCard>
    );
};

export const TransactionDescription = ({ title, children }: { title: string; children: ReactNode }) => (
    <Stack sx={{ gap: 0.5 }}>
        <Typography variant="h3">{title}</Typography>
        <Typography>{children}</Typography>
    </Stack>
);

export const TransactionSummary = ({ children }: { children: ReactNode }) => (
    <Stack
        direction={'column'}
        sx={{
            border: '1px solid',
            borderColor: 'divider',
            '&>*': { p: 4 },
            '&>*:not(:first-child)': { borderTop: '1px solid', borderColor: 'inherit' },
        }}
    >
        {children}
    </Stack>
);
